"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { useLocale } from "@/lib/labels/LabelsProvider";
import { LocaleCode, LOCALE_TO_SLUG, LOCALE_NAMES, LOCALE_DISPLAY } from "@/lib/labels/types";
import { persistLocaleChoice } from "@/features/locale-selector/persistLocaleChoice";

const LOCALE_FLAGS: Record<LocaleCode, string> = {
    en: "🇬🇧",
    es: "🇪🇸",
    "pt-BR": "🇧🇷",
    pt: "🇵🇹",
    bg: "🇧🇬",
    el: "🇬🇷",
    de: "🇩🇪",
    fr: "🇫🇷",
    it: "🇮🇹",
    ro: "🇷🇴",
    th: "🇹🇭",
    tr: "🇹🇷",
    vi: "🇻🇳",
    zh: "🇨🇳",
    ko: "🇰🇷",
    da: "🇩🇰",
    cs: "🇨🇿",
    sq: "🇦🇱",
    nl: "🇳🇱",
};

interface LanguageSwitcherProps {
    enabledLocales: LocaleCode[];
    variant?: "compact" | "full" | "dropdown" | "list";
    /** Text mode for list variant — "light" for dark backgrounds, "dark" for light backgrounds */
    textMode?: "light" | "dark";
}

export function LanguageSwitcher({ enabledLocales, variant = "compact", textMode = "light" }: LanguageSwitcherProps) {
    const currentLocale = useLocale();
    const pathname = usePathname();

    // Don't render for single-language tenants
    if (enabledLocales.length <= 1) {
        return null;
    }

    // Detect actual locale from pathname by checking all enabled locales
    let pathWithoutLocale = pathname;
    let detectedLocale = currentLocale;

    for (const locale of enabledLocales) {
        const slug = LOCALE_TO_SLUG[locale];
        const regex = new RegExp(`^/${slug}(/|$)`);
        if (regex.test(pathname)) {
            pathWithoutLocale = pathname.replace(regex, "/");
            detectedLocale = locale;
            break;
        }
    }

    // Ensure pathWithoutLocale is at least "/"
    if (!pathWithoutLocale || pathWithoutLocale === "") {
        pathWithoutLocale = "/";
    }

    // Auto-switch to dropdown when there are more than 3 languages
    const resolvedVariant = variant === "compact" && enabledLocales.length > 3 ? "dropdown" : variant;

    // List variant — flat links for use inside mobile drawers
    if (resolvedVariant === "list") {
        const activeClass =
            textMode === "light" ? "bg-white/20 font-medium" : "bg-foreground/15 font-medium";
        const hoverClass = textMode === "light" ? "hover:bg-white/10" : "hover:bg-foreground/10";

        return (
            <div className="flex flex-col gap-1">
                {enabledLocales.map((locale) => {
                    const slug = LOCALE_TO_SLUG[locale];
                    const href = `/${slug}${pathWithoutLocale === "/" ? "" : pathWithoutLocale}`;
                    const isActive = locale === detectedLocale;

                    return (
                        <a
                            key={locale}
                            href={href}
                            onClick={() => persistLocaleChoice(locale)}
                            className={`flex items-center gap-2.5 px-4 py-2 rounded-lg transition ${
                                isActive ? activeClass : hoverClass
                            }`}
                        >
                            <span>{LOCALE_FLAGS[locale]}</span>
                            <span className="text-sm">{LOCALE_NAMES[locale]}</span>
                        </a>
                    );
                })}
            </div>
        );
    }

    if (resolvedVariant === "dropdown") {
        return (
            <LanguageDropdown
                enabledLocales={enabledLocales}
                currentLocale={detectedLocale}
                pathWithoutLocale={pathWithoutLocale}
            />
        );
    }

    // Compact or full variant - inline buttons
    return (
        <div className="flex items-center gap-1">
            {enabledLocales.map((locale) => {
                const slug = LOCALE_TO_SLUG[locale];
                const href = `/${slug}${pathWithoutLocale === "/" ? "" : pathWithoutLocale}`;
                const isActive = locale === detectedLocale;
                const label = resolvedVariant === "full" ? LOCALE_NAMES[locale] : LOCALE_DISPLAY[locale];

                return (
                    // Use <a> instead of Link to force hard navigation
                    // This ensures the layout re-renders with the new locale's labels
                    <a
                        key={locale}
                        href={href}
                        onClick={() => persistLocaleChoice(locale)}
                        className={`px-2 py-1 text-sm rounded transition ${
                            isActive ? "bg-white/20 font-bold" : "hover:bg-white/10"
                        }`}
                    >
                        {label}
                    </a>
                );
            })}
        </div>
    );
}

function LanguageDropdown({
    enabledLocales,
    currentLocale,
    pathWithoutLocale,
}: {
    enabledLocales: LocaleCode[];
    currentLocale: LocaleCode;
    pathWithoutLocale: string;
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const close = useCallback(() => setOpen(false), []);

    // Close on outside click
    useEffect(() => {
        if (!open) return;

        function handleClick(e: MouseEvent) {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                close();
            }
        }

        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [open, close]);

    // Close on Escape
    useEffect(() => {
        if (!open) return;

        function handleKey(e: KeyboardEvent) {
            if (e.key === "Escape") close();
        }

        document.addEventListener("keydown", handleKey);
        return () => document.removeEventListener("keydown", handleKey);
    }, [open, close]);

    return (
        <div ref={ref} className="relative">
            <button
                onClick={() => setOpen((prev) => !prev)}
                className="btn-header flex items-center gap-1.5 px-2 py-1 text-sm cursor-pointer"
                aria-expanded={open}
                aria-haspopup="listbox"
            >
                <span>{LOCALE_FLAGS[currentLocale]}</span>
                <span>{LOCALE_DISPLAY[currentLocale]}</span>
                <ChevronDownIcon className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <div
                    className="absolute right-0 top-full mt-1 min-w-[14rem] rounded-[var(--radius)] border border-border bg-card text-card-foreground py-1 shadow-md z-50"
                    role="listbox"
                    aria-label="Select language"
                >
                    {enabledLocales.map((locale) => {
                        const slug = LOCALE_TO_SLUG[locale];
                        const href = `/${slug}${pathWithoutLocale === "/" ? "" : pathWithoutLocale}`;
                        const isActive = locale === currentLocale;

                        return (
                            // Use <a> instead of Link to force hard navigation
                            // This ensures the layout re-renders with the new locale's labels
                            <a
                                key={locale}
                                href={href}
                                role="option"
                                aria-selected={isActive}
                                className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer transition-colors hover:bg-muted ${
                                    isActive ? "bg-muted font-medium" : ""
                                }`}
                                onClick={() => {
                                    persistLocaleChoice(locale);
                                    close();
                                }}
                            >
                                <span>{LOCALE_FLAGS[locale]}</span>
                                <span>{LOCALE_NAMES[locale]}</span>
                            </a>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function ChevronDownIcon({ className = "" }: { className?: string }) {
    return (
        <svg className={`w-4 h-4 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
    );
}
